import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import FullCalendar from '@fullcalendar/react'
import interactionPlugin from '@fullcalendar/interaction'
import momentPlugin from '@fullcalendar/moment'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { Tooltip } from 'react-tooltip'
import * as XLSX from 'xlsx-js-style'

import '../Schedule/Schedule.scss'
import config from '../../config'
import { TIMELINE, COLORS_JOB_TYPE, COLORS_SHIFT } from '../../const'
import useGroupingEmployees from '../../hooks/useGroupingEmployees'
import { loadEmployeesAll } from '../../store/settings/actions'
import { getShiftTypes } from '../../store/shiftsTypes/actions'
import { addSnackbar, dismissSnackbar } from '../../store/organizationList/actions'
import { getJobTypes } from '../../store/jobTypes/actions'
import usePermissions from '../../components/Core/usePermissions'
import { getCompanyTimeOffRequests, getCompanyTimeOffPolicies, publishSchedule, notifySchedule, scheduleChangesLog, generateAIShiftEvents, savePlaceNote } from '../../api'
import createScheduleWorkbook from '../../helpers/schedule_export'

import HolidayIcon from '../../components/Core/HolidayIcon/HolidayIcon'
import Progress from '../../components/Core/Progress'
import MainLayout from '../../components/Core/MainLayout'
import CustomSelect from '../../components/Core/Select/Select'
import ButtonGroupToggle from '../../components/Core/ButtonGroupToggle'
import ResourceAreaHeader from '../Schedule/ResourceAreaHeader'
import EventContent from '../Schedule/EventContent'
import ResourceItem from '../Schedule/ResourceItem'
import DialogDeleteShift from '../../components/Core/Dialog/DeleteShift'
import DialogClearShift from '../../components/Core/Dialog/ClearShift'
import Footer from '../Schedule/Footer'
import CopyTool from '../Schedule/CopyTool'
import ToolsButton from '../../components/Core/ToolsButton/ToolsButton'
import FlatButton from '../../components/Core/FlatButton/FlatButton'
import ArrowEIPIcon from '../../components/Icons/ArrowEIPIcon'
import ExcelIcon from '../../components/Icons/ExcelIcon'
import CopyIcon from '../../components/Icons/CopyIcon'
import Button from '../../components/Core/Button/Button'
import MonthCell from './MonthCell'
import AddTempEmployee from '../Schedule/AddTempEmployee'
import DialogPublishShift from '../../components/Core/Dialog/PublishShift'
import ChangeLog from '../../components/Core/Dialog/ChangeLog';
import GenerationAIEventsSettings from '../../components/Core/Dialog/GenerationAIEventsSettings'
import DemandToolCell from './DemandToolCell'
import DemandToolSidebar from './DemandToolSidebar'
import EditComment from '../../components/Core/EditComment'
import EditTime from '../../components/Schedule/EditTime'
import CopySchedule from '../../components/CopySchedule'

const CALENDAR_VIEWS_CONFIG = {
  day: {
    type: 'resourceTimelineDay',
    title: 'ddd MMM, DD, YYYY',
    slotLabelFormat: 'HH:mm',
    slotDuration: '1:00',
    snapDuration: '00:15',
  },
  week: {
    type: 'resourceTimelineWeek',
    slotDuration: '24:00',
    snapDuration: '6:00',
  },
  month: {
    type: 'resourceTimeline',
    // slotDuration: '12:00',
  }
}

const permissionsConfig = [
  {
    name: 'cost',
    module: 'cost_earning',
  },
  {
    name: 'schedule_costs',
    module: 'schedule_shift',
    permission: 'schedule_costs',
  },
  {
    name: 'schedule_create_and_edit',
    module: 'schedule_shift',
    permission: 'schedule_create_and_edit',
  },
  {
    name: 'night_rates',
    module: 'night_rates',
  },
  {
    name: 'demand_tool',
    module: 'demand_tool',
  }, {
    name: 'can_edit_history',
    permission: 'schedule_can_edit_history',
  }
]

const request = async (url, method, params) => {
  if (method === 'GET' && params) {
    url += '?' + new URLSearchParams(params).toString()
  }
  const res = await fetch(
    `${config.api.url}/company/${url}`,
    {
      method: method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: method !== 'GET' ? JSON.stringify(params) : null,
    }
  )
  const data = await res.json()
  return data
}

const employToCheck = ({id,name,surname}) => ({
  id,
  label: `${name} ${surname}`,
})

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

const rangesOverlap = (start1, end1, start2, end2) => {
  if (end1.isBefore(start1)) [start1, end1] = [end1, start1]
  if (end2.isBefore(start2)) [start2, end2] = [end2, start2]
  return start1.isBefore(end2) && start2.isBefore(end1)
}

const handleResourceLabelClassNames = ({ resource }) => {
  const { extendedProps: props } = resource;
  const classes = []
  if (props.lastShift) {
    classes.push('fc-datagrid-cell-last-shift');
  }
  if (props.lastJobType) {
    classes.push('fc-datagrid-cell-last-job-type');
  }
  if (props.place_id && !props.employeeId) {
    classes.push('fc-datagrid-cell-place');
  } else if (props.shiftId) {
    classes.push('fc-datagrid-cell-shift');
  } else if (props.job_type_id) {
    classes.push('fc-datagrid-cell-job-type');
  } else if (props.employeeId || props.employeeId === 0) {
    classes.push('fc-datagrid-cell-employee');
  }
  if (props.employee_type === 3 || props.employee_type === 2){
    classes.push('fc-datagrid-cell-empty');
  }
  if (props.type === 'demand_tool') {
    classes.push('fc-datagrid-cell-demand-tool');
  }
  return classes
}

const updateWidthCell = (rows) => {
  const scheduleFooter = document.getElementById('schedule-footer')
  if (scheduleFooter?.getAttribute('data-timeline') === TIMELINE.WEEK) {
    Array.from(rows).forEach((itemJ, index) => {
      const { width } = itemJ.getBoundingClientRect()
      
      if (scheduleFooter.children[index + 1]) {
        scheduleFooter.children[index + 1].style.width = `${width}px`
      }
    })
  }

  const allElements = document.querySelectorAll('.fc-timeline-slot')
  allElements.forEach((el) => {
    el.colSpan = 1
  })
  
  if (scheduleFooter?.getAttribute('data-timeline') === TIMELINE.MONTH) {
    
    const elments = document.querySelectorAll('.statistic-slot')
    elments.forEach((el) => {
      el.colSpan = 2
    })
  }
}

const generateStatisticEvents = (date, events) => {
  const totalTimeDate = moment(date).endOf('month').add(1, 'days')
  const totalCostDate = moment(date).endOf('month').add(2, 'days')
  const resourceEvents = events.reduce((acc, event) => {
    return {
      ...acc,
      [event.resourceId]: [...(acc[event.resourceId] || []), event]
    }
  }, {})

  const temp = Object.entries(resourceEvents).flatMap(([resourceId, events]) => {
    const accumulatedData = events.filter(e => !e.empty_event && !e.empty_manual && !e.empty_employee).reduce((acc, e) => {
      if (e.timeOffRequest && e.timeOffRequest.status === 'approved') {
        return acc
      }
      return {
        totalHours: acc.totalHours + (e.hours || 0),
        nightHours: acc.nightHours + (e.night_duration || 0),
        cost: acc.cost + (e.cost*1 || 0),
      }
    }, {
      totalHours: 0,
      nightHours: 0,
      cost: 0,
    })
    const totalTimeEvent = {
      start: totalTimeDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: totalTimeDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      statistic: true,
      resourceId: resourceId,
      accumulatedData: accumulatedData,
    }
    const totalCostEvent = {
      start: totalCostDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: totalCostDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      statistic: true,
      resourceId: resourceId,
      accumulatedData: {
        cost: accumulatedData.cost,
      },
    }
    return [totalTimeEvent, totalCostEvent]
  })

  return temp
}

const getShiftsFromResources = (resources) => {
  return resources.reduce((acc, resource) => {
    if (resource.shiftId) {
      return [...acc, {
        shiftId: resource.shiftId,
        shiftStart: resource.shift_start,
        title: resource.title,

      }]
    } else if (resource.children && resource.children.length > 0) {
      return [...acc, ...getShiftsFromResources(resource.children)]
    }
    return acc
  }, [])
}

const generateEvents = (events, markers, timeline, scheduleSettings, fromDate) => {
  let result = []
  events = events.map(e => ({...e, realStart: e.start, realEnd: e.end}))
  if (scheduleSettings.remove_timelines && timeline === TIMELINE.WEEK) {
    result = events.map((e) => ({
      ...e,
      // realStart: e.start,
      // realEnd: e.end,
      start: e.empty_manual ? e.start : moment(e.start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: e.empty_manual ? e.end : moment(e.start).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    }));
  } else if (timeline === TIMELINE.DAY) {
    result = events
      .filter((e) => !(!e.employee_id && !moment(e.start).isSame(moment(fromDate), 'day')))
      .map(e => {
        const startOfDay = moment().startOf('day')
        const editable = moment(e.start, 'YYYY-MM-DD HH:mm:ss').isSameOrAfter(startOfDay) && !e.rId
        if (e.empty_manual && scheduleSettings.working_at_night) {
          const start = moment(e.start, 'YYYY-MM-DD HH:mm:ss')
          const startStr = `${start.format('YYYY-MM-DD')} ${scheduleSettings.time_view_stats}:00`
          const endStr = `${moment(startStr).add(24, 'hours').format('YYYY-MM-DD HH:mm:ss')}`
          const times = {
            start: startStr,
            end: endStr,
          }
          
          return {
            ...e,
            identifier: e.id,
            durationEditable: editable,
            ...times,
          }
        }
        return {
          ...e,
          identifier: e.id,
          durationEditable: editable
        }
      })
  } else {
    result = events
  }

  if (timeline === TIMELINE.WEEK) {
    result = result.map(e => {
      return {
        ...e,
        // realStart: e.start,
        // realEnd: e.end,
        ...(e.timeOffRequest && e.timeOffRequest.status === 'approved' ? {start: moment(e.start).startOf('day').format('YYYY-MM-DD HH:mm:ss'), end: moment(e.start).endOf('day').format('YYYY-MM-DD HH:mm:ss')} : {}),
      }
    })
  }

  if (timeline === TIMELINE.DAY) {
    result = result.map(e => {
      return {
        ...e,
        // realStart: e.start,
        // realEnd: e.end,
        ...(e.timeOffRequest && e.timeOffRequest.status === 'approved' ? {start: moment(e.start).startOf('day').format('YYYY-MM-DD HH:mm:ss'), end: moment(e.start).endOf('day').format('YYYY-MM-DD HH:mm:ss')} : {}),
      }
    })
  }

  if (timeline === TIMELINE.MONTH) {
    result = [...result, ...generateStatisticEvents(fromDate, events)].map(e => {
      const existMarker = markers.find(marker => {
        return moment(marker.date).format('YYYY-MM-DD') === moment(e.start).format('YYYY-MM-DD') && marker.employee_id === e.employee_id
      })
      
      return {
        ...e,
        // realStart: e.start,
        // realEnd: e.end,
        withConflicts: existMarker,
        end: moment(e.start).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      }
    })
  }
  return result
}

const ScheduleV2 = () => {
  const { id: companyId } = useParams()
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  const jobTypes = useSelector(state => state.jobTypes.jobTypes)
  const shiftsTypes = useSelector(state => state.shiftTypes)
  const { users: employees } = useSelector(state => state.settings.employees)
  const user = useSelector(state => state.auth.user)
  const copyToolHistory = useSelector(state => state.copyTool.history)
  const aiEventGeneration = useSelector(state => state.schedule.aiEventGeneration)
  const company = useSelector(state => state.company.companyInfo)

  const permissions = usePermissions(permissionsConfig)
  
  const [scheduleSettings, setScheduleSettings] = useState({
    use_publish: false,
    clock_in_restriction: false,
    ignore_clockin_restriction: false,
    working_at_night: false,
    place_timeline: false,
    use_publish: false,
    group_timeline: false,
    subgroup_timeline: false,
    shift_timeline: false,
    job_timeline: false,
    request_dayoff: true,
    use_empty_hours: true,
    ignore_empty_hours_logbook_edit: false,
    deduct_break: true,
    break_from_job: false,
    use_accumulated: true,
    accumulated_from_country: true,
    use_em_status: true,
    deduct_break_accumulated: false,
    accumulated_months: 1,
    accumulated_start: 1,
    start_finish: true,
    remove_timelines: true,
    work_night_excel: true,
    time_view_stats: "00:00",
    clock_in_minutes: 5,
    enabled_copy: false,
  })
  const [additionalRates, setAdditionalRates] = useState({
    night_time: true,
    night_time_time_start: "22:00",
    night_time_time_end: "06:00",
    night_time_type: 2,
    night_time_rate: 1.5,
    holiday: true,
    holiday_company: false,
    holiday_type: 2,
    holiday_rate: 2,
    holiday_night_type: 2,
    holiday_night_rate: 2,
    is_current: true,
    ignore_holiday_night_time: true
  })

  const [timeline, setTimeline] = useState(TIMELINE.MONTH)
  const [toolsActive, setToolsActive] = useState({ marking: false, start_finish: false, remove_timelines: false})
  const [filter, setFilter] = useState({employers: [], place: [], shiftType: []})
  const [currentStartDate, setCurrentStartDate] = useState(moment().startOf(timeline).format('YYYY-MM-DD'))
  const [schedule, setSchedule] = useState({holidays: {}, resources: [], events: [], markers: [], timesPanel: {}, demand_tools: {}, loading: true, published: false, count_changes: 0})
  const [workTimes, setWorkTimes] = useState({})
  const [copyTool, setCopyTool] = useState(false)
  const [copyToolTime, setCopyToolTime] = useState({})
  const [activeDrag, setActiveDrag] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [clearConfirmation, setClearConfirmation] = useState(false)
  const [deletedShiftName, setDeletedShiftName] = useState('')
  const [modalAddTempEmployee, setmodalAddTempEmployee] = useState(null)
  const [tempShiftID, setTempShiftID] = useState(0)
  const [tempEventID, setTempEventID] = useState(0)
  const [policies, setPolicies] = useState({})
  const [changeLogModal, setChangeLogModal] = useState(false)

  const calendarRef = useRef(null)
  const fromDateRef = useRef(new Date())
  const resizeObserverRef = useRef()
  const copyToolRef = useRef()
  const aiSettingsRef = useRef()
  const demandToolSidebarRef = useRef()
  const editCommentRef = useRef()
  const copyScheduleRef = useRef()

  const allSortedEmployees = useGroupingEmployees(employees, employToCheck)

  const [publishDialog,setPublishDialog] = useState(false)
  const count_changes = schedule?.count_changes || 0
  const resources = useMemo(() => {
    let currentColor = 0
    let colorType = 'bright'

    const updateChildren = (children, upLastShift, upLastJobType, upCustomTime) => {
      if (children) {
        return Object.values(children).map((item, index) => {
          const lastShift = upLastShift || (item.shiftId && ((children.length - 1) === index))
          const customTime = upCustomTime || item.custom_time
          const lastJobType = upLastJobType || (item.job_type_id && ((children.length - 1) === index))
          if (item.place_id && !item.employeeId && Array.isArray(item.children)) {
            item.children = item.children.reduce((acc, shift) => {
              if (schedule.demand_tools[shift.shiftId]) {
                acc.push({
                  title: t('Demand planning'),
                  id: `demand_tool_${shift.shiftId}`,
                  type: 'demand_tool',
                  sort: shift.sort,
                })
              }
              acc.push(shift)
              return acc
            }, [])
          }

          if (item.shiftId) {
            item.count = item.count || 0;
            item.children.forEach((i) => {
              item.count = item.count + i.children.length
              return i
            })
          }
          if (item.job_type_id) {
            item.count = item.children.length;
          }

          let eventBackgroundColor = item.color;
          let eventBorderColor = item.color;
          let lineColor = false;

          if (item.place_id){
            eventBorderColor = COLORS_JOB_TYPE[colorType][217];
            eventBackgroundColor = fade(COLORS_JOB_TYPE[colorType][217], 0.5);
          }
          if (item.shiftId) {
            colorType = COLORS_SHIFT.bright.some((itemC) => itemC === item.color) ? 'bright' : 'calm';
            eventBorderColor = COLORS_SHIFT[colorType][currentColor];
            eventBackgroundColor = COLORS_SHIFT[colorType][currentColor];
          }

          if (item.job_type_id) {
            if (currentColor >= COLORS_JOB_TYPE[colorType].length) {
              currentColor = 0;
            }

            eventBorderColor = COLORS_JOB_TYPE[colorType][currentColor];
            eventBackgroundColor = COLORS_JOB_TYPE[colorType][currentColor];
            currentColor += 1;
          }

          if (item.employeeId) {
            eventBorderColor = COLORS_JOB_TYPE[colorType][currentColor - 1];
            eventBackgroundColor = COLORS_JOB_TYPE[colorType][currentColor - 1] ? fade(COLORS_JOB_TYPE[colorType][currentColor - 1], 0.5) : '#3DB6F2'
          }
          if (item.employee_type === 3|| item.employee_type === 2 || item.empty_event) {
            eventBorderColor = COLORS_JOB_TYPE[colorType][216];
            eventBackgroundColor = fade(COLORS_JOB_TYPE[colorType][216], 0.5);
          }

          if ((scheduleSettings.remove_timelines && timeline === TIMELINE.WEEK) || timeline === TIMELINE.MONTH) {
            lineColor = eventBackgroundColor
            eventBorderColor = 'transparent'
            eventBackgroundColor = 'transparent'
          }

          if (item.timeOffRequest) {
            console.log(item)
          }
          
          return {
            ...item,
            eventBackgroundColor,
            eventBorderColor,
            lineColor,
            order: index,
            durationEditable: schedule.events[0]?.is_completed ? false : !!item.employeeId,
            lastShift: lastShift,
            lastJobType: lastJobType,
            children: updateChildren(item.children, lastShift, lastJobType, customTime),
            isPlace: item.place_id && !item.employeeId,
          }
        })
      }
      return []
    }

    return updateChildren(schedule.resources)
  }, [schedule.resources, schedule.events, scheduleSettings.remove_timelines, timeline])

  const events = useMemo(() => {
    const result = schedule.events.map(e => {
      const eventWeekDay = moment(e.start).isoWeekday()
      const isTimeOffRequested = e.timeOffRequest && (e.timeOffRequest.status === 'approved' || e.timeOffRequest.status === 'pending')
      return {
        ...e,
        ...(isTimeOffRequested ? {backgroundColor: 'transparent', borderColor: 'transparent'} : {}),
        defaultTimes: {
          start: workTimes[eventWeekDay] ? moment(workTimes[eventWeekDay].start, 'HH:mm').format('YYYY-MM-DD HH:mm:ss') : '08:00',
          end: workTimes[eventWeekDay] ? moment(workTimes[eventWeekDay].finish, 'HH:mm').format('YYYY-MM-DD HH:mm:ss') : '17:00',
        },
      }
    })

    return [
      ...result.filter((resultItem) => {
        return !copyToolHistory.some((historyItem) => {
          return moment(resultItem.start).format('YYYY-MM-DD') === moment(historyItem.start).format('YYYY-MM-DD') && resultItem.resourceId === historyItem.resourceId
        })
      }),
      ...copyToolHistory.map((e) => ({
        ...e,
        copy_event: true,
        realStart: e.start,
        realEnd: e.end,
        start: moment(e.start).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(e.start).endOf('day').format('YYYY-MM-DD HH:mm:ss')
      })),
    ]
  }, [schedule.events, copyToolHistory, workTimes, policies])

  const daysOfMonth = useMemo(() => {
    const currentMonth = moment().startOf('month')
    const day = currentMonth.clone().add(-1, 'days')
    const arr = new Array(currentMonth.daysInMonth()).fill().map((_, index) => {
      const dayNumber = day.add(1, 'days').day()
      const currentDay = moment()
  
      return {
        id: index + 1,
        title: index + 1,
        weekend: dayNumber === 6 || dayNumber === 0,
        today: currentDay.isSame(day, 'day'),
        holiday: schedule.holidays && schedule.holidays[index + 1] ? schedule.holidays[index + 1] : false,
        markers: schedule.markers.filter((marker) => day.isSame(moment(marker.date), 'day'))
      }
    })
    arr.push({
      id: 'totalTime',
      title: t('Total Time'),
      statistic: true,
    })
  
    if (permissions.cost && permissions.schedule_costs) {
      arr.push({
        id: 'totalCost',
        title: t('Total Cost'),
        statistic: true,
      })
    }
  
    return arr
  }, [permissions.cost, permissions.schedule_costs, schedule.holidays, schedule.markers])

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    const handler = () => {
      getSchedule({type: timeline, formDate: currentStartDate})
    }
    document.addEventListener('eventTimeChanged', handler)
    return () => {
      document.removeEventListener('eventTimeChanged', handler)
    }
  }, [currentStartDate]) 

  useEffect(() => {
    if (aiEventGeneration) {
      dispatch(dismissSnackbar())
      dispatch(addSnackbar(aiEventGeneration.message, 'success'))
      getSchedule({type: timeline, formDate: currentStartDate}).then(() => {
        dispatch({type: 'SET_AI_EVENT_GENERATION', payload: null})
        dispatch(dismissSnackbar())
      })
    }
  }, [aiEventGeneration?.uniqueId, timeline, currentStartDate])

  useEffect(() => {
    if (companyId) {
      init(companyId)
    }
  }, [companyId])

  useEffect(() => {
    if (!scheduleSettings.loaded) {
      return
    }      
    getSchedule({type: timeline, formDate: currentStartDate})
  }, [companyId, timeline, currentStartDate, filter, scheduleSettings.loaded])

  useEffect(() => {
    if (timeline === TIMELINE.WEEK || timeline === TIMELINE.MONTH) {
      handleViewDidMount()
    }
  }, [timeline, currentStartDate, schedule.loading, schedule.published])

  useEffect(() => {
    fromDateRef.current = moment(currentStartDate).toDate()
  }, [currentStartDate])

  const init = async (companyId) => {
    dispatch(loadEmployeesAll(companyId, {page: 'schedule'}))
    dispatch(getJobTypes(companyId))
    dispatch(getShiftTypes(companyId))
    const promisses = [
      request(`${companyId}/schedule/settings`),
      request(`${companyId}/logbook/additional-rates`),
      request(`${companyId}/work-time`),
      getCompanyTimeOffPolicies(companyId),
    ]
    const [scheduleSettingsRes, additionalRatesRes, workTimeRes, policiesRes] = await Promise.all(promisses)
    if (scheduleSettingsRes) {
      setScheduleSettings({...scheduleSettingsRes, loaded: true})
      setToolsActive(state => {
        return {
          ...state,
          remove_timelines: scheduleSettingsRes.remove_timelines,
          start_finish: scheduleSettingsRes.start_finish,
        }
      })
    }
    if (additionalRatesRes) {
      setAdditionalRates(additionalRatesRes)
    }
    if (Array.isArray(workTimeRes?.work_time?.work_days?.days)) {
      setWorkTimes(workTimeRes.work_time.work_days.days.reduce((acc, item) => ({
        ...acc,
        [item.day]: item
      }), {}))
    }
    if (Array.isArray(policiesRes?.policies)) {
      setPolicies(policiesRes.policies.reduce((acc, item) => ({
        ...acc,
        [item.id]: item
      }), {}))
    }
  }

  const changeView = (viewtype, date) => {
    calendarRef.current.getApi().changeView(viewtype, date)
    setTimeline(viewtype)
  }

  const getSchedule = async ({type, formDate}) => {
    const params = {
      type: type,
      from_date: formDate,
      shiftTypeArr: filter.shiftType.map(({id}) => id),
      employeesArr: filter.employers.map(({id}) => id),
      placesArr: filter.place.map(({id}) => id),
    }
    setSchedule(prev => ({...prev, loading: true}))
    const res = await request(`${companyId}/shift`, 'GET', params)
    if (res.success) {
      const events = res.events.map(e => {
        if (e.rId) {
          e.resourceId = e.rId
        }
        return e
      })
      let timeOffRequestsMap = {}
      const timeOffRes = await getCompanyTimeOffRequests(companyId, {start_date: formDate, view: 'monthly'})
      if (timeOffRes) {
        timeOffRequestsMap = timeOffRes.request_behalf.reduce((acc, req) => ({
          ...acc,
          [req.employee_id]: acc[req.employee_id] ? [...acc[req.employee_id], req] : [req]
        }), {})
      }

      const eventsWithRequests = events.map(e => {
        const timeOffRequests = timeOffRequestsMap[e.employee_id]
        if (timeOffRequests) {
          const conflictRequest = timeOffRequests.find(req => {
            const reqStart = moment(req.from).startOf('day')
            const reqEnd = moment(req.to).endOf('day')
            const eventStart = moment(e.start)
            const eventEnd = moment(e.end)
            return rangesOverlap(reqStart, reqEnd, eventStart, eventEnd)
          })
          if (conflictRequest) {
            return {
              ...e,
              timeOffRequest: conflictRequest,
            }
          }
        }
        return e
      })
      const workEvents = generateEvents(eventsWithRequests, res.markers, type, scheduleSettings, fromDateRef.current)
      // const demandToolsEvents = generateDemandToolsEvents(type, formDate, res.demand_tools, res.resources, events, res.demand_tools)
      
      setSchedule(state => ({
        accumulatedHours: res.accumulatedHours,
        holidays: Object.keys(res.holidays).length ? res.holidays : {},
        events: [...workEvents, ...(res.demand_tools_events || [])], // , ...demandToolsEvents
        markers: res.markers,
        resources: res.resources,
        timesPanel: res.timesPanel,
        published: res.published,
        count_changes: res.count_changes,
        demand_tools: (res.demand_tools_events || []).reduce((acc, event) => {
          const shiftId = event.resourceId.split('_')[2]
          return {
            ...acc,
            [shiftId]: true,
          }
        }, {}),
        loading: false,
      }))
    }
  }

  const handleOpenChangeLog = () => {
    setChangeLogModal(true);
  }

  const handleNotifyChanges = () => {
    notifySchedule(
      companyId,
      {date: moment(fromDateRef.current).format('YYYY-MM-DD') },
    ).then(() => {
      getSchedule({type: timeline, formDate: currentStartDate})
    });
  }

  const handlePublishSchedule = () => {
    setPublishDialog(false)
    publishSchedule(
      companyId,
      {date: moment(fromDateRef.current).format('YYYY-MM-DD') },
    ).then(() => {
      getSchedule({type: timeline, formDate: currentStartDate})
    });
  }

  const handlePublishDialog = () => {
    setPublishDialog(false);
  };
  const cancelPublish = () => {
    setPublishDialog(false);
  };
  
  const handleViewDidMount = (info) => {
    const container = document.getElementsByClassName('fc-timeline-slots')
    resizeObserverRef.current = new ResizeObserver((item) => {
      const rows = item[0].target.children[0].children[1].children[0].children
      updateWidthCell(rows)
    }).observe(container[0], { box: 'border-box' });

    if (!permissions.schedule_create_and_edit && !schedule?.published && !schedule.loading) {
      const calendarEl = info ? info.el.closest('.fc') : document.querySelector('.fc-view').closest('.fc');
      const eventArea = calendarEl.querySelector('.fc-timeline-body');
      let parentParent = null;
      if (eventArea) {
        parentParent = eventArea.parentElement?.parentElement;
      }
      
      // Remove any previous "No events" message
      const existingOverlay = calendarEl.querySelector('.no-events-overlay');
      if (existingOverlay) existingOverlay.remove();

      if (events.length === 0 && parentParent) {
        // Create overlay container
        const overlay = document.createElement("div");
        overlay.classList.add("no-events-overlay");
  
        // Create NotPublished element (you can replace this with your own React component if needed)
        const notPublished = document.createElement("div");
        notPublished.classList.add("not-published-icon");
  
        // Create title
        const title = document.createElement("p");
        title.classList.add("empty-title");
        title.innerText = t("WAITING FOR PUBLISHING");
  
        // Create descriptions
        const desc1 = document.createElement("p");
        desc1.classList.add("empty-description");
        desc1.innerText = t("This month is not yet published, your managers are still planning and scheduling work for this month.");
  
        const desc2 = document.createElement("p");
        desc2.classList.add("empty-description");
        desc2.innerText = t("You will be notified in the Grownu mobile app when this month will be published.");
  
        // Append all elements inside overlay
        overlay.appendChild(notPublished); // Append the NotPublished element
        overlay.appendChild(title);
        overlay.appendChild(desc1);
        overlay.appendChild(desc2);
  
        // Insert overlay inside parentParent
        parentParent.appendChild(overlay);
      }
    }
  }
  
  const getResourceTitle = (viewType, date) => {
    switch (viewType) {
      case 'day':
        return `${t(date.format('dddd'))}, ${t(date.format('MMMM'))}${date.format(' D, YYYY')}`
      case 'week':
        const startOfWeek = date.clone().startOf('isoWeek')
        const endOfWeek = date.clone().endOf('isoWeek')
        return `${t(startOfWeek.format('MMM'))} ${startOfWeek.format('D')} – ${endOfWeek.format('D, YYYY')}`
      case 'month':
        return capitalize(`${t(date.format('MMMM'))} ${date.format('YYYY')}`)
      default: {
        return ''
      }
    }
  }

  const onPlaceSelectFilter = (place) => {
    const arrChecked = place?.filter((i) => i.checked)
    setFilter((prevState) => ({
      ...prevState,
      place: arrChecked,
    }))
  }

  const onShiftSelectFilter = (shift) => {
    const arrChecked = shift?.filter((i) => i.checked)
    setFilter((prevState) => ({
      ...prevState,
      shiftType: arrChecked,
    }))
  }

  const onEmployeesSelectFilter = (emp) => {
    const arrChecked = emp?.filter((i) => i.checked)
    setFilter((prevState) => ({
      ...prevState,
      employers: arrChecked,
    }))
  }

  const handleChangeTimeline = (view) => {
    const startDate = view === TIMELINE.MONTH ? moment().startOf('month') : view === TIMELINE.WEEK ? moment().startOf('isoWeek') : moment()
    changeView(view, startDate.toDate())
    setCurrentStartDate(startDate.format('YYYY-MM-DD'))
  }

  const handleEventClassNames = (info) => {
    let classes = []
    if (info.event.extendedProps.empty_manual) {
      classes.push('is-empty-manual')
    }
    if (copyTool || info.event.extendedProps.copy_event) {
      classes.push('disable-drag')
    }
    return classes
  }

  const handeSlotLaneClassNames = (info) => {
    const date = moment(info.date);
    const holiday = (schedule?.holidays) ? schedule?.holidays[date.date()] : false;
    const h = (holiday && holiday[0] && holiday[0]?.date) ? holiday[0] : {};
    let result = '';
    const isNextMonth = date.isAfter(moment(currentStartDate).endOf('month'))
    if (isNextMonth && timeline === TIMELINE.MONTH) {
      return 'statistic-slot'
    }
    if (timeline !== TIMELINE.DAY && h.date) {
      result += 'cell_holiday ';
      if (h.company_work_time_id) {
        result += 'cell_holiday_company';
      } else if ((h.date && !h.company_work_time_id)) {
        result += 'cell_holiday_government';
        
      }
    }

    const isPastDay = date.isBefore(moment().startOf('day'))

    if (isPastDay) {
      result += ' cell_past_day'
    }
    
    
    return result;
  }

  const eventListener = (dropInfo, event) => {
    const start = moment(dropInfo.start)
    const end = moment(dropInfo.end)
    setSchedule(prev => ({
      ...prev,
      events: prev.events.map(e => {
        if (event.extendedProps.identifier === e.id) {
          return {
            ...e,
            title: `${start.format('HH:mm')}-${end.format('HH:mm')}`,
            start: start.format('YYYY-MM-DD HH:mm'),
            end: end.format('YYYY-MM-DD HH:mm'),
          }
        }
        return e
      })
    }))
    return true
  }

  const handleEventChange = ({ event }) => {
    const resourceInfo = event.getResources()[0]
    const [shiftId] = resourceInfo.id.split('-')
    handleChangeWorkingTime({
      id: event.id,
      shiftId,
      time: {
        start: moment(event.start),
        end: moment(event.end),
      },
    })
  }

  const handleEventChangeStart  = ({ event }) => {
    setActiveDrag(event.getResources()[0]?.id)
  }
  
  const handleEventChangeStop  = () => {
    setActiveDrag('')
  }

  const handleCopyTool = (time) => {
    setCopyToolTime(time)
    setCopyTool(!copyTool)
  }

  const handleCloseCopyTool = () => {
    setCopyTool(false)
  }

  const handleAddHistory = (data) => {
    copyToolRef.current.addHistory(data)
  }

  const handleAddTimelinesHistory = async history => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/add_timelines`, 'PATCH', {
      data: history,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleClickDay = (date) => {
    const calendarApi = calendarRef.current?.getApi()
    if (calendarApi) {
      calendarApi.changeView(TIMELINE.DAY, date.format('YYYY-MM-DD'))
      setCurrentStartDate(date.format('YYYY-MM-DD'))
      setTimeline(TIMELINE.DAY)
    }
  }

  const handleChangeWorkingTime = async ({shiftId, id, time}) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/change/timeline`, 'PATCH', {
      dateTime_start: time.start.format('YYYY-MM-DD HH:mm'),
      dateTime_end: time.end.format('YYYY-MM-DD HH:mm'),
      data: id,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleChangeEmployee = async ({employeeId, shiftId, id}) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/change/employee`, 'PATCH', {
      employee_id: employeeId,
      data: id,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleAddWorkingTime = async ({ shiftId, id, time }) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/add/timeline`, 'PATCH', {
      dateTime_start: time.start.format('YYYY-MM-DD HH:mm'),
      dateTime_end: time.end.format('YYYY-MM-DD HH:mm'),
      data: id,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleDeleteShift = async (shiftId) => {
    handleCloseDialog()
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/delete/${shiftId}`, 'DELETE')
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleDeleteTimeline = async ({ id, shiftId }) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/delete/timeline/${id}`, 'DELETE')
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleGenerateTimes = async (shiftId, employeeId) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/generate-times`, 'PATCH', {
      from_date: currentStartDate,
      type: timeline,
      employee_id: employeeId,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleClearTimes = async (shiftId, employeeId) => {
    setClearConfirmation(false)
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/${shiftId}/clear-times`, 'PATCH', {
      from_date: currentStartDate,
      type: timeline,
      employee_id: employeeId,
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleMarker = useCallback(async (employeeId, date) => {
    await request(`${companyId}/marker`, 'PATCH', {
      employeeId,
      date: date.format('YYYY-MM-DD'),
    })
    getSchedule({type: timeline, formDate: moment(fromDateRef.current).format('YYYY-MM-DD')})
  }, [companyId])

  const addTempEmployeeDispatch = async (selectedEmployee) => {
    setSchedule(prev => ({...prev, loading: true}))
    await request(`${companyId}/shift/add-assistant/${tempShiftID}`, 'POST', {
      employee_id: selectedEmployee,
      data: tempEventID
    })
    getSchedule({type: timeline, formDate: currentStartDate})
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setDeletedShiftName('')
  }

  const handleChangeTool = async ({ target: {name, checked} }) => {
    setToolsActive(state => ({...state, [name]: checked }))
    if (name === 'start_finish' || name ==='remove_timelines') {
      setSchedule(prev => ({...prev, loading: true}))
      const res = await request(`${companyId}/schedule/settings/edit`, 'POST', { ...toolsActive, [name]: checked })
      if (res) {
        const settingsRes = await request(`${companyId}/schedule/settings`)
        if (settingsRes) {
          setScheduleSettings(state => ({...state, ...settingsRes}))
        }
      }
      setSchedule(prev => ({...prev, loading: false}))
    }
  }

  const downloadScheduleFile = async (type) => {
    if ([181, 195].includes(Number(companyId))) {
      const events = schedule.events.filter(e => !e.empty_manual && !e.empty_event && !e.statistic && e.type !== 'demand_tool').reduce((acc, e) => {
        const event = {
          start: e.start,
          end: e.end,
          worked_hours: e.hours,
          night_hours: e.night_minutes,
          total_hours: e.hours + e.night_minutes,
        }
        return {
          ...acc,
          [e.employee_id]: acc[e.employee_id] ? {
            ...acc[e.employee_id],
            events: [...acc[e.employee_id].events, event],
          } : {
            employee_id: e.employee_id,
            employee_name: (e.new_employee || e.old_employee).name,
            employee_skill: e.skill ? e.skill[0] : '',
            hourly_rate: e.hours_demand,
            events: [event],
          }
        }
      }, {})

      const result = createScheduleWorkbook({
        title: t('Work schedule').toUpperCase(),
        companyName: company.name,
        groupedData: events,
        monthTitle: t('{{year}} {{month}}', { year: moment(fromDateRef.current).format('YYYY'), month: moment(fromDateRef.current).format('MMMM').toUpperCase() }),
        weekdayLabels: [t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')],
        sheetName: moment(fromDateRef.current).format('MMMM, YYYY').toUpperCase(),
        year: moment(fromDateRef.current).format('YYYY'),
        month: moment(fromDateRef.current).format('MM'),
        description: t('pradžios. Jei darbuotojas dirba 12 valandų per dieną per visą darbo laiką suteikiamos dvi pertraukos po 1 valandą, pertraukų eiga suderinama žodžiu su darbuotoju.')
      }, t)
      const filename = `${moment(fromDateRef.current).format('YYYY-MM-DD')}_${moment().format('HH-mm-ss')}-${company.name}.xlsx`;
      XLSX.writeFile(result.workbook, filename, {bookType: 'xlsx'})
      return
    }
    
    let nextFromDate = moment(fromDateRef.current)
    if (timeline === TIMELINE.WEEK) {
      nextFromDate = nextFromDate.startOf('isoWeek')
    }
    const data = {
      downloadType: type,
      shiftTypeArr: filter?.shiftType.map(({id}) => id),
      employeesArr: filter?.employers.map(({id}) => id),
      placesArr: filter?.place.map(({id}) => id),
    }
    const res = await request(`${companyId}/shift/download?type=${timeline}&from_date=${nextFromDate.format('YYYY-MM-DD')}`, 'POST', data)
    const link = document.createElement('a')
    link.setAttribute('download', res.file)
    link.setAttribute('target', '_blank')
    link.href = `${res.path}`
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handleCreateNewShift = () => {
    history.push(`/${companyId}/schedule/shift/create`)
  }

  const handleGenerateAIEvents = (shiftId) => {
    aiSettingsRef.current.open(shiftId)
  }

  const generateAIEvents = async (shiftId) => {
    setSchedule(prev => ({...prev, loading: true}))
    const res = await generateAIShiftEvents(companyId, shiftId, {
      from_date: currentStartDate,
      type: timeline,
    })
    if (res.message) {
      dispatch(addSnackbar(res.message, 'success'))
      await new Promise((resolve) => setTimeout(resolve, 1500))
      dispatch(dismissSnackbar())
      return
    }
    setSchedule(prev => ({...prev, loading: false}))
  }

  const getShiftName = (id) => {
    const name = shiftsTypes?.shiftTypes?.find((i) => i.id === id)?.name
    return name || ''
  }

  const addTempEmployees =  (shiftId, eventId) => {
    setmodalAddTempEmployee(data => !data)
    setTempShiftID(shiftId)
    setTempEventID(eventId)
  }

  const handlePressPlaceNote = (placeId, note) => {
    // console.log('handlePressPlaceNote', placeId, note)
    editCommentRef.current.open({value: note, data: {placeId}})
  }

  const handleSavePlaceNote = async (form) => {
    const res = await savePlaceNote(companyId, form.data.placeId, {text: form.value, month: moment(fromDateRef.current).format('YYYY-MM')})
    if (res) {
      setSchedule(prev => ({
        ...prev,
        resources: prev.resources.map(r => {
          return r.place_id === form.data.placeId ? {...r, note: form.value} : r
        })
      }))
      editCommentRef.current.close()
    }
  }

  const checkIfEventsExist = useCallback((shiftId, employeeId) => {
    if (!events || !shiftId) return false;

    const selectedEvent = events.find((e) => {
      const ids = e.resourceId.toString().split('-');
      const check = ids && ids[0] && ids[0].toString() === shiftId.toString() && (!employeeId  || employeeId.toString() === (e?.employee_id || '' ).toString()) && e.employee_id && !e.empty_employee && !e.empty_event && !e.empty_manual
      if (check) {
        if (e.start === e.end) {
          return false
        }
      }
      return check
    })
    
    return !!selectedEvent
  }, [events])

  const unavailableEmployees = useCallback(() => {
    const selectedEvent = events.find(e => e.id == tempEventID)
    if (selectedEvent) {
      const allEmployees = events.filter(e => e.empty_employee === false && e.resourceId.indexOf(tempShiftID+'-') == 0 && selectedEvent.day_number == e.day_number)
      return allEmployees.map(e => e?.new_employee?.id ? e?.new_employee?.id*1 : e.employee_id*1)
    }
    return []
  }, [events, tempShiftID, tempEventID])

  const handleCopySchedule = () => {
    copyScheduleRef.current.open()
  }

  const renderResourceLaneContent = useCallback(({resource}) => {
    if (resource.extendedProps.employeeId) {
      const current_markers = schedule.markers.filter(m => m.employee_id*1 === resource.extendedProps.employeeId*1)
      if (current_markers.length) {
        const resourceEvents = schedule.events.filter(e => e.resourceId === resource.id && !e.empty_manual)
        const eventsByDate = resourceEvents.reduce((acc, e) => {
          return {
            ...acc,
            [moment(e.start).format('YYYY-MM-DD')]: true
          }
        }, {})
        return (
          <React.Fragment>
            {
              current_markers.map((marker, i) => {
                const existEvent = eventsByDate[moment(marker.date).format('YYYY-MM-DD')]
                if (existEvent) {
                  return null
                }
                const date_header = document.querySelector('.fc-timeline-slot-label[data-date^="'+moment(marker.date).format('yyyy-MM-DD')+'"]')
                if (!date_header) {
                  return null
                }
                return (
                  <div key={i} data-tooltip-id='user_marker' data-tooltip-html={marker.comment} className="fc-markers-item marked" style={{ width: date_header.offsetWidth, left: date_header.offsetLeft+1 }} data-mark={moment(marker.date).format('yyyy-MM-DD')}>
                  </div>
                )
              })
            }
          </React.Fragment>
        )
      }
    }
    return <React.Fragment />
  }, [schedule.markers, schedule.events])

  const renderMonthHeader = useCallback(({date: monthDate}) => {
    const date = moment(monthDate)
    const holiday = schedule.holidays[date.date()]
    const isNextMonth = date.isAfter(moment(currentStartDate).endOf('month'))

    if (isNextMonth) {
      if (date.date() === 1) {
        const wordsTotalTime = t('Total Time').split(' ')
        return (
          <>
            {
              wordsTotalTime.map((w, i) => {
                return <div key={i} style={{fontSize: 11}}>{w}</div>
              })
            }
          </>
        )
      } else {
        const wordsTotalCost = t('Total Cost').split(' ')
        return (
          <>
            {
              wordsTotalCost.map((w, i) => {
                return <div key={i} style={{fontSize: 11}}>{w}</div>
              })
            }
          </>
        )
      }
    }

    return (
      <div onClick={() => handleClickDay(date)}>
        <span className='schedule-enter-day'>{ t('Go') }</span>
        { `${date.format('D')}` }
        <HolidayIcon holidays={holiday} month={true} />
      </div>
    )
  }, [schedule.holidays, currentStartDate])

  const renderWeekHeader = useCallback(({date: weekDate}) => {
    const date = moment(weekDate)
    const holiday = schedule.holidays[date.date()]

    return (
      <div onClick={() => handleClickDay(date)}>
        <span className='schedule-enter-day'>{ t('Enter') }</span>
        { `${t(date.format('ddd'))}${date.format(', DD')}` }
        <HolidayIcon holidays={holiday} month={true} />
      </div>
    )
  }, [schedule.holidays])

  const renderResourceAreaHeaderContent = useCallback(({view}) => {
    const handleClickPrev = () => {
      view.calendar.prev()
      const targetDate = view.getCurrentData().currentDate
      if (timeline === TIMELINE.MONTH) {
        
        const date = moment(fromDateRef.current).startOf('month').subtract(1, 'days').startOf('month')
        fromDateRef.current = date.startOf('day').toDate()
        setCurrentStartDate(date.format('YYYY-MM-DD'))
      } else {
        fromDateRef.current = moment(targetDate).startOf('day').toDate()
        setCurrentStartDate(moment(targetDate).format('YYYY-MM-DD'))
      }
    }

    const handleClickNext = () => {
      view.calendar.next()
      const targetDate = view.getCurrentData().currentDate
      if (timeline === TIMELINE.MONTH) {
        const date = moment(fromDateRef.current).endOf('month').add(1, 'days')
        fromDateRef.current = date.startOf('day').toDate()
        setCurrentStartDate(date.format('YYYY-MM-DD'))
      } else {
        fromDateRef.current = moment(targetDate).startOf('day').toDate()
        setCurrentStartDate(moment(targetDate).format('YYYY-MM-DD'))
      }
    }

    

    const inputDate = moment(currentStartDate)

    const holiday = (view.type === 'day' && schedule?.holidays) ? schedule?.holidays[inputDate.date()] : false
    const title = getResourceTitle(view.type, inputDate)
    return (
      <ResourceAreaHeader
        title={title}
        holiday={holiday}
        onClickPrev={handleClickPrev}
        onClickNext={handleClickNext} />
    )
  }, [schedule.holidays, timeline, currentStartDate])

  const renderSlotLaneContent = useCallback(({date}) => {
    const d = moment(date)
    const isWeekend = d.day() === 6 || d.day() === 0
    if (isWeekend) {
      return (
        <div
          className="weekend-slot"
        />
      )
    }
    return <div />
  }, [])

  const renderResourceLabelContent = useCallback(({fieldValue, resource}) => {
    const {
      photo,
      shiftId,
      employeeId,
      employeesCount,
      jobTypeId,
      isPlace,
      note,
      place_id,
    } = resource.extendedProps
    const realCount = employeesCount
    const accumulatedHoursDetected = schedule.accumulatedHours[employeeId+'-'+jobTypeId] ? schedule.accumulatedHours[employeeId+'-'+jobTypeId] : (schedule.accumulatedHours[employeeId] ? schedule.accumulatedHours[employeeId] : []);
    const [employeeShiftId] = resource._resource.id.toString().split('-')

    const isEmployeeAssignedToShift = (() => {
      return user.employee?.shifts.some(shift => shift.id === shiftId*1 || shift.id === employeeShiftId*1)
    })()

    return (
      <ResourceItem
        title={`${fieldValue} ${realCount ? `(${realCount})` : ''}`} // ${resource.id}
        photo={photo}
        templateId={resource.extendedProps.template_id}
        accumulatedHours={accumulatedHoursDetected}
        shiftId={shiftId}
        placeId={place_id}
        isPlace={isPlace}
        note={note}
        withMenu={(permissions.schedule_create_and_edit || isEmployeeAssignedToShift) && (shiftId || employeeId && resource.extendedProps.template_id && timeline === TIMELINE.MONTH)}
        employeeId={employeeId}
        enableCopy={scheduleSettings.enabled_copy}
        onEditShift={() => history.push(`/${companyId}/schedule/shift/${resource.extendedProps.template_id || shiftId}`)}
        onCopyShift={() => copyScheduleRef.current.open({id: shiftId, name: fieldValue})}
        withAI={permissions.demand_tool && schedule.demand_tools[shiftId]}
        onDeleteShift={() => {
          setOpenDialog(shiftId)
          setDeletedShiftName(fieldValue)
        }}
        onPressNote={handlePressPlaceNote}
        onGenerateAiEvents={handleGenerateAIEvents}
        canClearTimesForShift={shiftId && resource.extendedProps.template_id && checkIfEventsExist(shiftId, employeeId)}
        onGenerateTimes={checkIfEventsExist(employeeShiftId, employeeId) ? false : () => handleGenerateTimes(shiftId || employeeShiftId, employeeId)}
        onClearTimes={(fullShift) => {
          if (fullShift) {
            setClearConfirmation({shiftId: shiftId, employeeId: employeeId, shift: getShiftName(shiftId), month: moment(fromDateRef.current).format('MMMM')})
            return
          }
          setClearConfirmation({shiftId: employeeShiftId, employeeId: employeeId, shift: getShiftName(employeeShiftId), month: moment(fromDateRef.current).format('MMMM')})
        }}
      />
    );
  }, [schedule.accumulatedHours, permissions.schedule_create_and_edit, timeline])

  const renderEventContent = useCallback(({event, timeText, view}) => {
    if (schedule.loading) {
      return <div></div>
    }
    
    const resourceInfo = event.getResources()[0]
    const selectedEvent = event.extendedProps
    const start = (scheduleSettings.remove_timelines && timeline !== TIMELINE.DAY && selectedEvent.realStart) ? selectedEvent.realStart : event.start
    const end = (scheduleSettings.remove_timelines && timeline !== TIMELINE.DAY && selectedEvent.realEnd) ? selectedEvent.realEnd : ((timeline === TIMELINE.MONTH && selectedEvent.realEnd) ? selectedEvent.realEnd : event.end)
    const employeeName = resourceInfo.title
    const withMenu = resourceInfo.extendedProps.employeeId || resourceInfo?.extendedProps?.employeeId === 0
    const shiftId = resourceInfo.extendedProps.shift_id ? resourceInfo.extendedProps.shift_id : resourceInfo.id.split('-')[0]

    const isAssignedUserToShift = (shift_id) => {
      return user?.employee?.shifts.some(shift => shift.id === shift_id*1)
    }
    
    const isCanEdit = (() => {
      const place_id = selectedEvent?.place_id;
      const splited = resourceInfo?.id?.toString().split('-');
      const job_type_id = splited && splited[1] ? splited[1] : null;
      const shift_id = splited && splited[0] ? splited[0] : null;
      if (user?.employee?.place?.[0]?.id) {
        if (place_id && user?.employee?.place?.[0]?.id.toString() === place_id.toString()) {
          if (user?.employee?.shifts?.length) {
            if (shift_id && isAssignedUserToShift(shift_id)) {
              if (user?.employee?.job_type_id) {
                if (job_type_id && user?.employee?.job_type_id.toString() === job_type_id.toString()) {
                  return true;
                } else {
                  return false;
                }
              } else {
                return true;
              }
            } else {
              return false;
            }
          } else {
            return true;
          }
        } else {
          return false
        }
      }
  
      return permissions.schedule_create_and_edit
    })()

    const unEmployees = schedule.events.reduce((acc, e) => {
      if (e.empty_employee === false && e.resourceId.indexOf(shiftId+'-') === 0 && selectedEvent.day_number === e.day_number) {
        return [...acc, Number(e?.new_employee?.id ? e?.new_employee?.id : e.employee_id)]
      }
      return acc
    }, [])

    if (selectedEvent.type === 'demand_tool') {
      return (
        <DemandToolCell
          event={selectedEvent}
          resourceId={resourceInfo.id}
          // activeDemandId={activeDemandTool}
          onSelect={(id, data) => {
            // setActiveDemandTool(prev => prev === id ? null : id)
            dispatch({type: 'SET_ACTIVE_DEMAND_ID', id: id})
            demandToolSidebarRef.current.open(data)
          }} />
      )
    }

    const isNextMonth = moment(start).isAfter(moment(currentStartDate).endOf('month'))
    if (isNextMonth) {
      if (moment(start).date() === 1 && 'totalHours' in (selectedEvent.accumulatedData || {})) {
        return (
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontFamily: "Helvetica Neue Medium"}}>
            <div style={{color: '#333945', fontSize: 11, fontWeight: 'bold'}}>{selectedEvent.accumulatedData.totalHours}</div>
            <div style={{color: '#db894f', fontSize: 11, fontWeight: 'bold', borderTop: '1px solid #db894f'}}>{selectedEvent.accumulatedData?.nightHours}h</div>
          </div>
        )
      }
      if (moment(start).date() === 2 && 'cost' in (selectedEvent.accumulatedData || {})) {
        return (
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontFamily: "Helvetica Neue Medium"}}>
            <div style={{color: '#333945', fontSize: 11, fontWeight: 'bold'}}>
              { selectedEvent.accumulatedData.cost.toFixed(2) }
            </div>
          </div>
        )
      }
      if (timeline === TIMELINE.MONTH) {
        return (
          <div />
        )
      }
    }

    if (timeline === TIMELINE.MONTH) {
      return (
        <MonthCell
          selectedEvent={{...selectedEvent, shiftId, id: event.id, companyId: companyId}}
          schedule={scheduleSettings}
          id={event.id}
          shiftId={shiftId}
          employeeId={resourceInfo?.extendedProps?.employeeId}
          title={event.title}
          employeeName={employeeName}
          timeText={timeText}
          start={start}
          end={end}
          policies={policies}
          resourceId={resourceInfo.id}
          copy_event={event.extendedProps.copy_event}
          empty={event.extendedProps.empty_event}
          empty_manual={event.extendedProps.empty_manual}
          nightDuration={timeline === TIMELINE.MONTH && selectedEvent.night_duration}
          newEmployee={event.extendedProps.new_employee}
          oldEmployee={event.extendedProps.old_employee}
          cost={event.extendedProps.cost}
          night_minutes={event.extendedProps.night_minutes}
          break_minutes={event.extendedProps.break_minutes}
          work_minutes={event.extendedProps.work_minutes}
          minutes={selectedEvent.minutes}
          costPermission={permissions.cost && permissions.schedule_costs}
          nightPermission={permissions.night_rates && additionalRates.night_time}
          viewType={view.type}
          photo={resourceInfo.extendedProps.photo}
          withMenu={withMenu && !copyTool && isCanEdit}
          jobTypeName={resourceInfo.extendedProps.job_type_name}
          copyTool={copyTool}
          endDay={event.endStr}
          editPermissions={isCanEdit}
          isCompleted={event?._def?.extendedProps?.is_completed}
          isCanEditHistory={permissions.can_edit_history}
          // isCanEditHistory={true}
          activeDrag={activeDrag === resourceInfo.id}
          unavailableEmployees={unEmployees}
          markers={schedule.markers}
          removeTimelines={(scheduleSettings.remove_timelines && timeline === TIMELINE.WEEK) || timeline === TIMELINE.MONTH}
          showHoursCount={timeline === TIMELINE.MONTH && !scheduleSettings.start_finish}
          lineColor={resourceInfo?.extendedProps?.lineColor}
          onChangeWorkingTime={handleChangeWorkingTime}
          onChangeEmployee={handleChangeEmployee}
          addTimeline={handleAddWorkingTime}
          onDeleteTimeline={handleDeleteTimeline}
          handleCopyTool={handleCopyTool}
          handleAddHistory={handleAddHistory}
          isMarkerMode={toolsActive.marking}
          addEmployee={() => addTempEmployees(shiftId, event.id)}
          handleMarker={handleMarker} />
      )
    }
    return (
      <EventContent
        id={event.id}
        selectedEvent={selectedEvent}
        shiftId={shiftId}
        employeeId={resourceInfo?.extendedProps?.employeeId}
        title={event.title}
        employeeName={employeeName}
        timeText={timeText}
        start={selectedEvent.realStart ? selectedEvent.realStart : event.start}
        end={selectedEvent.realEnd ? selectedEvent.realEnd : event.end}
        policies={policies}
        resourceId={resourceInfo.id}
        copy_event={event.extendedProps.copy_event}
        empty={event.extendedProps.empty_event}
        empty_manual={event.extendedProps.empty_manual}
        nightDuration={timeline === TIMELINE.MONTH && selectedEvent.night_duration}
        newEmployee={event.extendedProps.new_employee}
        oldEmployee={event.extendedProps.old_employee}
        cost={event.extendedProps.cost}
        night_minutes={event.extendedProps.night_minutes}
        break_minutes={event.extendedProps.break_minutes}
        work_minutes={event.extendedProps.work_minutes}
        minutes={event.extendedProps.minutes}
        costPermission={permissions.cost && permissions.schedule_costs}
        nightPermission={permissions.night_rates && additionalRates.night_time}
        viewType={view.type}
        photo={resourceInfo.extendedProps.photo}
        withMenu={withMenu && !copyTool && isCanEdit}
        isCanEditHistory={permissions.can_edit_history}
          // isCanEditHistory={true}
        jobTypeName={resourceInfo.extendedProps.job_type_name}
        copyTool={copyTool}
        endDay={event.endStr}
        editPermissions={isCanEdit}
        isCompleted={event?._def?.extendedProps?.is_completed}
        activeDrag={activeDrag === resourceInfo.id}
        unavailableEmployees={unEmployees}
        markers={schedule.markers}
        removeTimelines={(scheduleSettings.remove_timelines && timeline === TIMELINE.WEEK) || timeline === TIMELINE.MONTH}
        showHoursCount={timeline === TIMELINE.MONTH && !scheduleSettings.start_finish}
        lineColor={resourceInfo?.extendedProps?.lineColor}
        onChangeWorkingTime={handleChangeWorkingTime}
        onChangeEmployee={handleChangeEmployee}
        addTimeline={handleAddWorkingTime}
        onDeleteTimeline={handleDeleteTimeline}
        handleCopyTool={handleCopyTool}
        handleAddHistory={handleAddHistory}
        addEmployee={() => addTempEmployees(shiftId, event.id)}
      />
    )
  }, [permissions, scheduleSettings, timeline, activeDrag, schedule, copyTool, toolsActive.marking, policies])

  return (
    <MainLayout>
      <div className='schedule-screen'>
        <div className='schedule-screen__header'>
          <CustomSelect
            placeholder={t('All job types')}
            buttonLabel={t('Filter')}
            items={jobTypes}
            onChange={onPlaceSelectFilter}
            width='auto'
            withSearch={true} />
          <CustomSelect
            placeholder={t('All shifts')}
            buttonLabel={t('Filter')}
            items={shiftsTypes?.shiftTypes}
            onChange={onShiftSelectFilter}
            width='auto'
            withSearch={true} />
          <CustomSelect
            placeholder={t('All employees')}
            buttonLabel={t('Filter')}
            items={allSortedEmployees ?? []}
            onChange={onEmployeesSelectFilter}
            width='auto'
            withSearch={true} />
          <ButtonGroupToggle
            buttons={[
              {
                label: t('Day'),
                id: TIMELINE.DAY,
              },
              {
                label: t('Week'),
                id: TIMELINE.WEEK,
              },
              {
                label: t('Month'),
                id: TIMELINE.MONTH,
              },
            ]}
            onChange={handleChangeTimeline}
            value={timeline}
          />
          {
            !copyTool
              ? <ToolsButton
                  withLog={permissions.schedule_create_and_edit}
                  handleOpenChangeLog={handleOpenChangeLog}
                  handleInputChange={handleChangeTool}
                  values={toolsActive}
                  style={{height: '100%'}}
                />
              : null
          }
          {
            timeline === TIMELINE.MONTH
              ? <FlatButton onClick={() => downloadScheduleFile('excel')} className='schedule-screen__buttonDownload'>
                  <ArrowEIPIcon className='schedule-screen__buttonArrow' /> <ExcelIcon />
                </FlatButton>
              : null
          }

          <div className='schedule-screen__buttons'>
            {
              scheduleSettings.enabled_copy
                ? <Button
                    className={'simple-schedule-screen__publish'}
                    onClick={handleCopySchedule}
                  >
                    <CopyIcon />
                  </Button>
                : null
            }
            {
              timeline === TIMELINE.MONTH && schedule && permissions.schedule_create_and_edit && scheduleSettings.use_publish ? (
                ! schedule?.published && !schedule?.events?.length ? (
                  <Button
                    className={'simple-schedule-screen__nochanges'}
                    disabled
                  >
                    {t('No Entries')}
                  </Button>
                ) : (
                  ! schedule?.published && schedule?.events?.length ? (
                    <Button
                      className={'simple-schedule-screen__publish'}
                      onClick={() => { setPublishDialog(true) }}
                    >
                      {t('Publish')}
                    </Button>
                  ) : (
                    schedule?.published && !count_changes ? (
                      <Button
                        className={'simple-schedule-screen__published'}
                        disabled
                      >
                        {t('Published')}
                      </Button>
                    ) : (
                      schedule?.published && count_changes ? (
                        <Button
                          className={'simple-schedule-screen__notify'}
                          onClick={handleNotifyChanges}
                        >
                          {t('Notify Changes')} ({count_changes})
                        </Button>
                      ) : null
                    )
                  )
                )

              ) : null
            }
            {
              !copyTool && permissions.schedule_create_and_edit
                ? <Button onClick={handleCreateNewShift}>
                    {t('Create new shift')}
                  </Button>
                : null
            }
          </div>
        </div>
        <div className="schedule-screen">
          <div style={{display: 'flex', flexDirection: 'row', flex: 1}}>
            <div style={{width: '100%', height: '100%'}}>
              <FullCalendar
                ref={calendarRef}
                initialView={timeline}
                firstDay={1}
                schedulerLicenseKey='CC-Attribution-NonCommercial-NoDerivatives'
                plugins={[resourceTimelinePlugin, resourceDayGridPlugin, interactionPlugin, momentPlugin]}
                headerToolbar={false}
                aspectRatio={1}
                height="100%"
                eventStartEditable={false}
                // eventMinWidth={10}
                // dayMinWidth={10}
                slotMinWidth={10}
                // slotMaxWidth={10}
                eventDurationEditable={timeline === TIMELINE.DAY}
                resourceAreaWidth={timeline === TIMELINE.MONTH ? '20%' : '25%'}
                views={{
                  ...CALENDAR_VIEWS_CONFIG,
                  day: {
                    ...CALENDAR_VIEWS_CONFIG.day,
                    slotMinTime: scheduleSettings.working_at_night ? scheduleSettings.time_view_stats : '00:00:00',
                    slotMaxTime: scheduleSettings.working_at_night ? `${+scheduleSettings.time_view_stats.split(':')[0] + 24}:00:00` : '24:00:00', 
                  },
                  week: {
                    ...CALENDAR_VIEWS_CONFIG.week,
                    slotLabelFormat: renderWeekHeader,
                  },
                  month: {
                    ...CALENDAR_VIEWS_CONFIG.month,
                    slotLabelContent: renderMonthHeader,
                    slotLabelClassNames: ({date: monthDate, view}) => {
                      const isNextMonth = moment(monthDate).isAfter(moment(currentStartDate).endOf('month'))
                      const date = moment(monthDate)
                      const holiday = schedule.holidays[date.date()]
                      const isWeekend = date.day() === 6 || date.day() === 0
                      return isNextMonth ? ['statistic-slot'] : holiday ? [isWeekend ? 'header-holiday-slot-weekend' : 'header-holiday-slot'] : []
                    },
                    visibleRange: () => {
                      const visibleRange = {
                        start: moment(currentStartDate).clone().startOf('month').toDate(),
                        end: moment(currentStartDate).clone().endOf('month').add(permissions.cost && permissions.schedule_costs ? 2 : 1, 'days').toDate(),
                      }
                      return visibleRange
                    },
                  },
                }}
                resourceOrder="order"
                resources={resources}
                events={events}
                eventContent={renderEventContent}
                eventClassNames={handleEventClassNames}
                slotLaneClassNames={handeSlotLaneClassNames}
                slotLaneContent={renderSlotLaneContent}
                resourceAreaHeaderClassNames={() => ['resource-area-header']}
                resourceAreaHeaderContent={renderResourceAreaHeaderContent}
                resourceLaneContent={renderResourceLaneContent}
                resourceLabelClassNames={handleResourceLabelClassNames}
                resourceLabelContent={renderResourceLabelContent}
                locale={localStorage.getItem('i18nextLng') || 'en'}
                eventResize={handleEventChange}
                eventAllow={eventListener}
                eventResizeStart={handleEventChangeStart}
                eventResizeStop={handleEventChangeStop}
                viewDidMount={handleViewDidMount}
              />
            </div>
          </div>
          <Footer
            timeline={timeline}
            data={schedule.timesPanel}
            daysOfMonth={daysOfMonth}
            withCost={permissions.cost && permissions.schedule_costs} />
          {
            schedule.loading
              ? <div className='schedule-screen__overlay-loading'>
                  <Progress />
                </div>
              : null
          }
        </div>
      </div>
      <Tooltip
        id='time_off'
        className={'schedule-screen__tooltip'}
        effect='solid' />
      <Tooltip
        id='place_note'
        className={'schedule-screen__tooltip'}
        place="right"
        effect='solid' />
      <Tooltip
        id='time_active'
        className='schedule-screen__tooltip schedule-screen__tooltip__active'
        effect='solid' />
      <Tooltip
        id='time'
        className='schedule-screen__tooltip'
        effect='solid' />
      <Tooltip
        id='time_past'
        className='schedule-screen__tooltip schedule-screen__tooltip__past'
        effect='solid' />
      <Tooltip
        id='time_empty'
        className='schedule-screen__tooltip schedule-screen__tooltip__empty'
        effect='solid' />
      <Tooltip
        id='demand_hours'
        className='schedule-screen__tooltip schedule-screen__tooltip__demand'
        effect='solid' />
      <Tooltip
        id='holiday'
        className='schedule-screen__tooltip'
        effect='solid'
      />
      <Tooltip
        id='user_marker'
        className='schedule-screen__tooltip schedule-screen__tooltip__marker'
        effect='solid'
      />
      <Tooltip
        id='user_avatar'
        className='schedule-screen__tooltip schedule-screen__tooltip__avatar'
        arrowColor='transparent'
        style={{backgroundColor: 'transparent'}}
        place="right"
        effect='solid'
      />
      <GenerationAIEventsSettings
        ref={aiSettingsRef}
        companyId={companyId}
        onSubmit={generateAIEvents}/>
      <DialogDeleteShift
        open={openDialog}
        handleClose={handleCloseDialog}
        title={t('Delete Shift?')}
        buttonTitle2={t('Cancel')}
        buttonTitle={t('Delete')}
        shiftName={deletedShiftName}
        submitDeleteShift={() => handleDeleteShift(openDialog)}
        cancelDelete={handleCloseDialog} />
      <DialogClearShift
        open={!!clearConfirmation?.shiftId}
        handleClose={() => setClearConfirmation(false)}
        title={t('Clear Work Times?')}
        buttonTitle2={t('Cancel')}
        buttonTitle={t('Clear')}
        info={clearConfirmation}
        submitDeleteShift={() => handleClearTimes(clearConfirmation.shiftId, clearConfirmation.employeeId)}
        cancelDelete={() => setClearConfirmation(false)}
      />
      {
        modalAddTempEmployee
          ? <AddTempEmployee
              setmodalAddTempEmployee={setmodalAddTempEmployee}
              addTempEmployeeDispatch={addTempEmployeeDispatch}
              unavailableEmployees={unavailableEmployees()}
          />
          : null
      }
      {
        copyTool
          ? <CopyTool
              ref={copyToolRef}
              start={copyToolTime.start || null}
              end={copyToolTime.end || null}
              onClose={handleCloseCopyTool}
              onAddTimelines={handleAddTimelinesHistory}
            />
          : null
      }
      <DialogPublishShift
        open={publishDialog}
        handleClose={handlePublishDialog}
        title={t('Schedule Publishing')}
        description={
          <>
          {t('You are about to publish your scheduled calendar for all employees and notify them with push notification to their Grownu Mobile APP. They will start seeing what is planned for them for this month both in WEB and Mobile APP environments.')}
          <br/>
          {t("You are not able to revoke the process/unpublish so be sure that it's fully prepared.")}
          </>
        }
        buttonTitle2={t('Cancel')}
        buttonTitle={t('Publish')}
        submitDeleteShift={() => handlePublishSchedule(publishDialog)}
        cancelDelete={cancelPublish}
      />
      { changeLogModal && (
        <ChangeLog
          date={fromDateRef.current}
          type={'schedule'}
          open={changeLogModal}
          onClose={() => setChangeLogModal(false)}
        />
      )}
      <DemandToolSidebar
        ref={demandToolSidebarRef}
        onSubmit={() => getSchedule({type: timeline, formDate: currentStartDate})} />
      <EditComment
        ref={editCommentRef}
        onSubmit={handleSavePlaceNote}
      />
      <EditTime />
      <CopySchedule
        ref={copyScheduleRef}
        companyId={companyId}
        currentDate={currentStartDate}
        targetEvents={schedule.events}
        onCopy={() => getSchedule({type: timeline, formDate: currentStartDate})} />
    </MainLayout>
  )
}

export default ScheduleV2
